import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useDateRange } from "@/context/DateRangeContext";
import { fmtMoney, fmtNum } from "@/lib/format";
import { Loading } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, ChevronLeft, ChevronRight, Download, ChevronDown, Table2, Map as MapIcon, TrendingUp } from "lucide-react";
import { downloadCSV } from "@/lib/csv";
import ProductThumb, { imgSrc } from "@/components/ProductThumb";
import OrdersMap from "@/components/OrdersMap";

const PAGE = 25;

function TrafficPanel() {
  const { currency } = useCurrency();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/business-report?currency=${currency}`).then((r) => setD(r.data)).catch(() => setD({ data: [], totals: {} })); }, [currency]);
  if (!d) return <Loading />;
  return (
    <div className="space-y-4" data-testid="traffic-panel">
      <p className="text-sm text-[#A3A3A3]">Business Report data (sessions, units & conversion) — kept separate from Sales so it never inflates your revenue.</p>
      {!d.data.length ? (
        <div className="bg-[#0f0f0f] border border-white/5 rounded-xl p-10 text-center text-[#666]">No Business Report uploaded yet. Upload an Amazon "Business Report (by ASIN)" to see traffic & conversion here.</div>
      ) : (
        <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="traffic-table">
          <div className="p-4 border-b border-[#1a1a1a] flex flex-wrap gap-6 text-xs">
            <span className="text-[#666]">Products <span className="text-white font-semibold tnum ml-1">{fmtNum(d.totals.products || 0)}</span></span>
            <span className="text-[#666]">Sessions <span className="text-white font-semibold tnum ml-1">{fmtNum(d.totals.sessions || 0)}</span></span>
            <span className="text-[#666]">Units <span className="text-white font-semibold tnum ml-1">{fmtNum(d.totals.units || 0)}</span></span>
            <span className="text-[#666]">Conversion <span className="text-[#10B981] font-semibold tnum ml-1">{d.totals.conversion != null ? `${d.totals.conversion}%` : "—"}</span></span>
          </div>
          <div className="overflow-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#0f0f0f]"><tr className="text-left text-[#666] text-xs uppercase tracking-wider">
                <th className="px-5 py-3">Product</th><th className="px-5 py-3">ASIN</th><th className="px-5 py-3 text-right">Sessions</th><th className="px-5 py-3 text-right">Units</th><th className="px-5 py-3 text-right">Conversion</th><th className="px-5 py-3 text-right">Sales</th>
              </tr></thead>
              <tbody>
                {d.data.map((r) => (
                  <tr key={r.key} data-testid={`traffic-row-${r.key}`} className="border-t border-[#141414] hover:bg-white/5">
                    <td className="px-5 py-3 text-white max-w-[260px] truncate" title={r.name}>{r.name || r.sku || r.asin}</td>
                    <td className="px-5 py-3 text-[#A3A3A3] tnum text-xs">{r.asin || "—"}</td>
                    <td className="px-5 py-3 text-right tnum text-[#A3A3A3]">{fmtNum(r.sessions)}</td>
                    <td className="px-5 py-3 text-right tnum text-white font-semibold">{fmtNum(r.units)}</td>
                    <td className="px-5 py-3 text-right tnum text-[#10B981]">{r.conversion != null ? `${r.conversion}%` : "—"}</td>
                    <td className="px-5 py-3 text-right tnum text-[#A3A3A3]">{fmtMoney(r.revenue, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OrdersTab() {
  const { currency } = useCurrency();
  const { start, end } = useDateRange();
  const [view, setView] = useState("orders");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/analytics/orders?currency=${currency}&limit=${PAGE}&skip=${page * PAGE}&search=${encodeURIComponent(q)}&start=${start}&end=${end}`)
      .then((r) => { setRows(r.data.data); setTotal(r.data.total); setLoading(false); });
  }, [currency, page, q, start, end]);

  useEffect(() => { if (view === "orders") load(); }, [load, view]);

  const submitSearch = (e) => { e.preventDefault(); setPage(0); setQ(search); };
  const pages = Math.ceil(total / PAGE);

  const exportAll = async () => {
    const LIM = 500;
    let skip = 0, all = [], t = Infinity;
    while (skip < t) {
      const r = await api.get(`/analytics/orders?currency=${currency}&limit=${LIM}&skip=${skip}&search=${encodeURIComponent(q)}&start=${start}&end=${end}`);
      t = r.data.total;
      if (!r.data.data.length) break;
      all = all.concat(r.data.data); skip += LIM;
    }
    const out = all.map((o) => ({
      order_id: o.order_id, date: o.date, product_name: o.product_name, sku: o.sku,
      asin: o.asin, marketplace: o.sales_channel, country: o.ship_country, state: o.ship_state, city: o.ship_city,
      quantity: o.quantity, [`revenue_${currency}`]: o.revenue,
      [`fees_${currency}`]: o.fees ?? "", fees_estimated: o.fees_estimated ? "yes" : "no",
      [`net_profit_${currency}`]: o.net_profit ?? "", roi_pct: o.roi ?? "", status: o.status,
      image_url: imgSrc(o.image) || "",
    }));
    downloadCSV(`orders-${currency}.csv`, out);
  };

  const TABS = [{ id: "orders", label: "Orders", icon: Table2 }, { id: "map", label: "Map", icon: MapIcon }, { id: "traffic", label: "Traffic & Conversion", icon: TrendingUp }];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 bg-[#0f0f0f] border border-[#1a1a1a] rounded-full p-1 w-fit" data-testid="orders-view-switch">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`orders-view-${t.id}`} onClick={() => setView(t.id)}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === t.id ? "bg-[#007AFF] text-white" : "text-[#A3A3A3] hover:text-white"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {view === "map" ? <OrdersMap /> : view === "traffic" ? <TrafficPanel /> : (
      <>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <form onSubmit={submitSearch} className="flex items-center gap-2 max-w-md flex-1">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666]" />
            <Input data-testid="order-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order ID, product or SKU…" className="bg-[#111] border-[#262626] pl-9 text-white focus-visible:ring-[#007AFF]" />
          </div>
          <Button type="submit" data-testid="order-search-button" className="bg-[#007AFF] hover:bg-[#005BB5] rounded-full">Search</Button>
        </form>
        <Button data-testid="export-orders-csv" onClick={exportAll} variant="outline" className="h-10 bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a] hover:text-white rounded-full">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      {loading ? <Loading /> : (
        <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="orders-table">
          <div className="overflow-auto max-h-[65vh]" data-testid="orders-scroll">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-[#0f0f0f]"><tr className="text-left text-[#666] text-xs uppercase tracking-wider">
                <th className="px-5 py-3">Order ID</th><th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Product</th><th className="px-5 py-3">SKU</th><th className="px-5 py-3">Marketplace</th>
                <th className="px-5 py-3 text-right">Qty</th><th className="px-5 py-3 text-right">Revenue</th>
                <th className="px-5 py-3 text-right">Fees</th><th className="px-5 py-3 text-right">Net</th><th className="px-5 py-3 text-right">ROI</th><th className="px-5 py-3">Status</th>
              </tr></thead>
              <tbody>
                {rows.map((o) => {
                  const isOpen = expanded === o.id;
                  return (
                    <React.Fragment key={o.id}>
                      <tr data-testid={`order-row-${o.order_id}`} onClick={() => setExpanded(isOpen ? null : o.id)} className="border-t border-[#141414] hover:bg-white/5 transition-colors duration-150 cursor-pointer">
                        <td className="px-5 py-3 text-[#007AFF] tnum text-xs font-medium">
                          <span className="inline-flex items-center gap-1"><ChevronDown className={`h-3 w-3 text-[#666] transition-transform ${isOpen ? "rotate-180" : ""}`} />{o.order_id}</span>
                        </td>
                        <td className="px-5 py-3 text-[#A3A3A3] tnum text-xs">{o.date}</td>
                        <td className="px-5 py-3 text-white max-w-[200px]"><div className="flex items-center gap-2.5"><ProductThumb src={o.image} size={30} title={o.product_name} /><span className="truncate" title={o.product_name}>{o.product_name}</span></div></td>
                        <td className="px-5 py-3 text-[#A3A3A3] tnum text-xs" data-testid={`order-sku-${o.order_id}`}>{o.sku || "—"}</td>
                        <td className="px-5 py-3 text-[#A3A3A3] text-xs">{o.sales_channel}</td>
                        <td className="px-5 py-3 text-right tnum text-[#A3A3A3]">{o.quantity}</td>
                        <td className="px-5 py-3 text-right tnum text-white font-semibold">{fmtMoney(o.revenue, currency)}</td>
                        <td className="px-5 py-3 text-right tnum text-[#F59E0B]" data-testid={`order-fees-${o.order_id}`}>
                          <span className="inline-flex items-center gap-1 justify-end">{fmtMoney(o.fees, currency)}{o.fees_estimated && <span className="text-[9px] uppercase text-[#666] bg-[#222] px-1 rounded" title="Estimated — upload a Settlement report for exact fees">est</span>}</span>
                        </td>
                        <td className="px-5 py-3 text-right tnum font-semibold" style={{ color: o.net_profit == null ? "#555" : o.net_profit >= 0 ? "#10B981" : "#EF4444" }}>{o.net_profit == null ? "—" : fmtMoney(o.net_profit, currency)}</td>
                        <td className="px-5 py-3 text-right tnum font-semibold" data-testid={`order-roi-${o.order_id}`} style={{ color: o.roi == null ? "#555" : o.roi >= 0 ? "#10B981" : "#EF4444" }}>{o.roi == null ? "—" : `${o.roi}%`}</td>
                        <td className="px-5 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#10B981]/15 text-[#10B981]">{o.status}</span></td>
                      </tr>
                      {isOpen && o.fee_breakdown && (
                        <tr data-testid={`order-fee-detail-${o.order_id}`} className="bg-[#0b0b0b]">
                          <td colSpan={11} className="px-5 py-3">
                            <div className="flex flex-wrap gap-6 text-xs">
                              <span className="text-[#666]">{o.fees_estimated ? "Estimated fee breakdown:" : "Fee breakdown:"}</span>
                              <span className="text-[#A3A3A3]">Referral / Selling <span className="text-white font-semibold tnum ml-1">{fmtMoney(o.fee_breakdown.referral, currency)}</span></span>
                              <span className="text-[#A3A3A3]">FBA <span className="text-white font-semibold tnum ml-1">{fmtMoney(o.fee_breakdown.fba, currency)}</span></span>
                              <span className="text-[#A3A3A3]">Other <span className="text-white font-semibold tnum ml-1">{fmtMoney(o.fee_breakdown.other, currency)}</span></span>
                              <span className="text-[#A3A3A3]">COGS <span className="text-white font-semibold tnum ml-1">{fmtMoney(o.cogs, currency)}</span></span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {!rows.length && <tr><td colSpan={11} className="px-5 py-16 text-center text-[#666]">No orders found. Upload an "All Orders" report or load sample data.</td></tr>}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-[#1a1a1a]">
              <span className="text-xs text-[#666]">{total.toLocaleString()} orders · page {page + 1} of {pages}</span>
              <div className="flex gap-2">
                <Button data-testid="orders-prev" size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a]"><ChevronLeft className="h-4 w-4" /></Button>
                <Button data-testid="orders-next" size="sm" variant="outline" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} className="bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a]"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
}

